/**
 * src/shell/agents/services/AgentJobQueue.ts
 *
 * Asynchronous Background Job Execution Engine for Multi-Agent Workflows.
 * Provides live progress streaming, error isolation, and notification hooks.
 */

import { AgentJob, AgentJobStatus } from '../types/gstack-agents-types';

export class AgentJobQueue {
  private static instance: AgentJobQueue;
  private jobs: Map<string, AgentJob> = new Map();
  private listeners: Set<(jobs: AgentJob[]) => void> = new Set();
  private progressListeners: Map<string, Set<(progress: number, msg: string) => void>> = new Map();

  private constructor() {}

  public static getInstance(): AgentJobQueue {
    if (!AgentJobQueue.instance) {
      AgentJobQueue.instance = new AgentJobQueue();
    }
    return AgentJobQueue.instance;
  }

  public enqueueJob(
    agentId: 'gtm.landing_page_gen' | 'ops.weekly_retro' | 'sales.battle_card_gen',
    userId: string,
    tenantId: string,
    inputPayload: any
  ): AgentJob {
    const job: AgentJob = {
      id: `job_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      agentId,
      userId,
      tenantId,
      status: 'QUEUED',
      progress: 0,
      statusMessage: 'Queued in orchestrator buffer...',
      inputPayload,
      createdAt: Date.now(),
      estimatedCostUsd: 0,
      tokensUsed: 0,
    };

    this.jobs.set(job.id, job);
    this._notify();
    return job;
  }

  public updateProgress(jobId: string, progress: number, message: string): void {
    const job = this.jobs.get(jobId);
    if (!job) return;

    job.status = 'RUNNING';
    job.progress = Math.min(100, Math.max(0, progress));
    job.statusMessage = message;

    const progSet = this.progressListeners.get(jobId);
    if (progSet) {
      progSet.forEach((cb) => cb(job.progress, message));
    }
    this._notify();
  }

  public completeJob(jobId: string, result: any, costUsd = 0, tokens = 0): void {
    const job = this.jobs.get(jobId);
    if (!job) return;

    job.status = 'COMPLETED';
    job.progress = 100;
    job.statusMessage = 'Completed successfully.';
    job.result = result;
    job.completedAt = Date.now();
    job.estimatedCostUsd = costUsd;
    job.tokensUsed = tokens;

    this._notify();
  }

  public failJob(jobId: string, error: string): void {
    const job = this.jobs.get(jobId);
    if (!job) return;

    job.status = 'FAILED';
    job.statusMessage = `Failed: ${error}`;
    job.error = error;
    job.completedAt = Date.now();

    this._notify();
  }

  public getJob(jobId: string): AgentJob | undefined {
    return this.jobs.get(jobId);
  }

  public listJobs(userId?: string): AgentJob[] {
    const all = Array.from(this.jobs.values()).sort((a, b) => b.createdAt - a.createdAt);
    if (userId) return all.filter((j) => j.userId === userId);
    return all;
  }

  public subscribe(callback: (jobs: AgentJob[]) => void): () => void {
    this.listeners.add(callback);
    callback(this.listJobs());
    return () => this.listeners.delete(callback);
  }

  public subscribeToJob(jobId: string, callback: (progress: number, msg: string) => void): () => void {
    if (!this.progressListeners.has(jobId)) {
      this.progressListeners.set(jobId, new Set());
    }
    this.progressListeners.get(jobId)!.add(callback);
    const current = this.jobs.get(jobId);
    if (current) callback(current.progress, current.statusMessage);

    return () => {
      this.progressListeners.get(jobId)?.delete(callback);
    };
  }

  private _notify(): void {
    const list = this.listJobs();
    this.listeners.forEach((cb) => cb(list));
  }
}

export const agentJobQueue = AgentJobQueue.getInstance();
